/**
 * SOLID Review
 * S — Strong. One concern: the table's use cases and event handlers, built once over the table's collaborators.
 * O — Good. A new move is one use case built here; a new event one handler in `TableEventHandlers`.
 * L — N/A — no inheritance hierarchy.
 * I — Good. The table's ops of `ClientUseCases`, and its kinds of `ClientServerEvents`.
 * D — Good. Port interfaces and the shared steps, injected; what it builds is held by role (`IUseCase`).
 */
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { ICommandNothing, ICommandAnnounce, ICommandLeave } from '../../domain/ports/schemas/TableMoves';
import type { IPortClientState, IPortClientRoundServer, IPortClientRoomServer, IPortClientServerWatch } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { ClientSteps } from '../shared/ClientSteps';
import { ServerRoundTrip } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';
import { StartGameUseCase } from './StartGameUseCase';
import { RollDiceUseCase } from './RollDiceUseCase';
import { PeekDiceUseCase } from './PeekDiceUseCase';
import { AnnounceUseCase } from './AnnounceUseCase';
import { BelieveUseCase } from './BelieveUseCase';
import { RollFirstUseCase } from './RollFirstUseCase';
import { LiftCupUseCase } from './LiftCupUseCase';
import { PlayAgainUseCase } from './PlayAgainUseCase';
import { LeaveTableUseCase } from './LeaveTableUseCase';
import { LoadLobbyRoomUseCase } from './LoadLobbyRoomUseCase';
import { LobbyPollTickUseCase } from './LobbyPollTickUseCase';
import { WatchTableUseCase } from './WatchTableUseCase';
import { UnwatchTableUseCase } from './UnwatchTableUseCase';
import { ResyncUseCase } from './ResyncUseCase';
import { TableEventHandlers, type IConfigDepTableEvents } from './TableEventHandlers';

export interface IConfigDepTableContext {
  readonly decisions: IPortClientDecisions;
  readonly state: IPortClientState;
  readonly rounds: IPortClientRoundServer;
  readonly rooms: IPortClientRoomServer;
  readonly watch: IPortClientServerWatch;
  /** The one serial section, shared with the session (FE2). */
  readonly steps: ClientSteps;
}

/**
 * THE TABLE LOOP (frontend strict-ports plan, FE1, 2026-10-03; use cases and event handlers since frontend-application-oo
 * P2/P3). What used to be a dozen click handlers and subscription callbacks, each reading the store, calling an adapter and
 * writing the store in its own way, is one pattern:
 *
 *     step:   snapshot → decide → apply        (inside the SERIAL section, no I/O)
 *     move:   step(plan) → server call → step(answer)          — `ServerRoundTrip`, one use case per move
 *     event:  step(the event's decision)                       — `TableEventHandlers`, one handler per event
 *
 * THE SERIAL SECTION is `ClientSteps` (FE2: one queue for the table and the session). A server call sits BETWEEN two steps,
 * never inside one, so a slow server cannot hold the table; the second step re-reads the table. An event the guards drop
 * and a move the plan refuses are ordinary outcomes, not errors: a refused tap answers its refusal, and React ignores it.
 */
export class TableContext {
  // ── the local player's moves ──────────────────────────────────────────────────────────────────
  readonly startGame: IUseCase<ICommandNothing, TPortOutcome>;
  readonly rollDice: IUseCase<ICommandNothing, TPortOutcome>;
  readonly peekDice: IUseCase<ICommandNothing, TPortOutcome>;
  /** An open claim after a peek, a blind pass before one — the plan says which. */
  readonly announce: IUseCase<ICommandAnnounce, TPortOutcome>;
  readonly believe: IUseCase<ICommandNothing, TPortOutcome>;
  readonly rollFirst: IUseCase<ICommandNothing, TPortOutcome>;
  /** The plan clears the server's decider before the call; the table moves whatever the server says. */
  readonly liftCup: IUseCase<ICommandNothing, TPortOutcome>;
  /** The plan restores the lives before the call; the answer — or its absence — follows. */
  readonly playAgain: IUseCase<ICommandNothing, TPortOutcome>;
  /** Reset the table, then tell the server — best effort, never awaited. */
  readonly leaveTable: IUseCase<ICommandLeave, TPortOutcome>;
  // ── keeping the table level with the server ───────────────────────────────────────────────────
  /** The lobby's roster read on entry. */
  readonly loadLobbyRoom: IUseCase<ICommandNothing, TPortOutcome>;
  /** One tick of the lobby's self-heal poll. */
  readonly lobbyPollTick: IUseCase<ICommandNothing, TPortOutcome>;
  /** Watch the streams of the table as it stands: its room, and its round's. Idempotent downstream. */
  readonly watchTable: IUseCase<ICommandNothing, TPortOutcome>;
  readonly unwatchTable: IUseCase<ICommandNothing, TPortOutcome>;
  // ── the server's events ───────────────────────────────────────────────────────────────────────
  readonly events: TableEventHandlers;

  constructor(deps: IConfigDepTableContext) {
    const { steps } = deps;
    const table: IConfigDepTable = { ...deps, trip: new ServerRoundTrip(steps.onTable), step: steps.onTable };
    this.startGame = new StartGameUseCase(table);
    this.rollDice = new RollDiceUseCase(table);
    this.peekDice = new PeekDiceUseCase(table);
    this.announce = new AnnounceUseCase(table);
    this.believe = new BelieveUseCase(table);
    this.rollFirst = new RollFirstUseCase(table);
    this.liftCup = new LiftCupUseCase(table);
    this.playAgain = new PlayAgainUseCase(table);
    this.leaveTable = new LeaveTableUseCase(table);
    this.loadLobbyRoom = new LoadLobbyRoomUseCase(table);
    this.lobbyPollTick = new LobbyPollTickUseCase(table);
    this.watchTable = new WatchTableUseCase(table);
    this.unwatchTable = new UnwatchTableUseCase(table);
    const events: IConfigDepTableEvents = { decisions: deps.decisions, step: steps.onTable, resync: new ResyncUseCase(table) };
    this.events = new TableEventHandlers(events);
  }
}
